import RtcService from '../services/RtcService'
import CallService from '../services/CallService'
import MediaService from '../services/MediaService'
import { applyCoachLoadBalancing } from '../utils/applyCoachLoadBalancing'
import { markChatHandoffNavigation } from '../utils/chatHandoff'

// Main CandidateChatManager that coordinates between RtcService, CallService, and MediaService
class CandidateChatManager {
  constructor() {
    // Prevent multiple instances
    if (CandidateChatManager.instance) {
      return CandidateChatManager.instance
    }
    CandidateChatManager.instance = this

    // Initialize services
    this.rtcService = new RtcService()
    this.callService = new CallService()
    this.mediaService = new MediaService()

    // Initialize logging properties
    this.logChatEventPath = null
    this.logChatMissedOpportunityPath = null
    this.buildChatHistoryPath = null
    this.chatCoachInfoPath = null
    this.serverTimeOffset = 0
    // coachId -> full coach-info API payload
    this.coachInfo = {}
    this._coachInfoInflight = {}

    // Central connection state
    this.connectionState = {
      isConnected: false,
      coaches: [],
      candidates: [],
      selectedCoach: null,
      activeCall: null,
      chatRoom: null,
      myEasyrtcid: null,
      messages: [], // Add messages array
      chatMode: 'text',
      chatStatus: 'disconnected',
      hasLocalVideo: false,
      hasLocalAudio: false,
      hasRemoteVideo: false,
      hasRemoteAudio: false,
      localStream: null,
      remoteStream: null,
      connectionLost: false, // Track connection lost state
      deloitteUser: false, // Track user type for terminology
      disconnectionReason: null, // Track why connection was lost
      disconnectionTimestamp: null, // When disconnection occurred
    }

    // Event listeners
    this.listeners = new Map()

    // Track if left_chat action has been sent to prevent duplicates
    this._leftChatActionSent = false

    // Set up cross-manager communication
    this._setupManagerCommunication()

    // Set up frame communication
    this._setupFrameCommunication()

    // Initialize media service
    this._initializeMediaService()

    // Load persisted chat data if available (for page refreshes)
    this._loadPersistedChatData()

    // Set up unload handlers to send left_chat action
    this._setupUnloadHandlers()
  }

  /**
   * Sets up unload handlers to send left_chat action when candidate navigates away
   *
   * @private
   */
  _setupUnloadHandlers() {
    if (typeof window === 'undefined') return

    // Listen to RtcService beforeUnload/pageHide events
    this.rtcService.addEventListener('beforeUnload', () => {
      this._sendLeftChatAction()
    })

    this.rtcService.addEventListener('pageHide', () => {
      this._sendLeftChatAction()
    })
  }

  /**
   * Sends left_chat action to coach before disconnecting
   *
   * @private
   */
  _sendLeftChatAction() {
    // Prevent duplicate sends
    if (this._leftChatActionSent) {
      return
    }

    // Only send if we have an active call
    if (!this.connectionState.activeCall?.coachEasyrtcid) {
      return
    }

    const coachEasyrtcid = this.connectionState.activeCall.coachEasyrtcid
    const easyrtc = this.rtcService.getEasyRTC()

    if (easyrtc && coachEasyrtcid) {
      try {
        // Send left_chat action before disconnecting
        easyrtc.sendData(coachEasyrtcid, 'candidate_action', {
          action_type: 'left_chat',
        })
        // Mark as sent to prevent duplicates
        this._leftChatActionSent = true
      } catch (error) {
        console.warn(
          'CandidateChatManager - Error sending left_chat action in unload handler:',
          error,
        )
      }
    }
  }

  // Load persisted chat data from localStorage
  _loadPersistedChatData() {
    try {
      const lastChatId = this.getLastChatId()
      if (lastChatId) {
        const chatData = this.getChatDataFromSession(lastChatId)
        if (chatData && chatData.conversationId) {
          /* console.log(
            'CandidateChatManager - Loading persisted chat data for conversation:',
            lastChatId,
          ) */

          // Restore messages if available
          if (chatData.messages && chatData.messages.length > 0) {
            this.connectionState.messages = chatData.messages
          }

          // Restore selectedCoach if available
          if (chatData.selectedCoach) {
            this.connectionState.selectedCoach = chatData.selectedCoach
          }

          // Restore chatMode if available
          if (chatData.chatMode) {
            this.connectionState.chatMode = chatData.chatMode
          }

          // Restore disconnection information if available
          if (chatData.disconnectionReason) {
            this.connectionState.disconnectionReason =
              chatData.disconnectionReason
          }
          if (chatData.disconnectionTimestamp) {
            this.connectionState.disconnectionTimestamp =
              chatData.disconnectionTimestamp
          }
        }
      }
    } catch (error) {
      console.error(
        'CandidateChatManager - Error loading persisted chat data:',
        error,
      )
    }
  }

  _chatToken() {
    return (
      this.rtcService?.connectionState?.username ||
      this.connectionState.username ||
      (typeof window !== 'undefined' ? window.username : null) ||
      null
    )
  }

  // Event logging function (matches old chat.js pattern)
  logEvent(eventType, info, conversationId) {
    if (!this.logChatEventPath) {
      console.warn(
        'CandidateChatManager - logChatEventPath not set, cannot log event:',
        eventType,
      )
      return
    }

    const chatToken = this._chatToken()
    if (!chatToken) {
      console.warn(
        'CandidateChatManager - chat token not set, cannot log event:',
        eventType,
      )
      return
    }

    const ts = new Date().getTime()
    const nts = ts + this.serverTimeOffset
    const params = {
      event_type: eventType,
      occurred_at: ts,
      event_info: info,
      chat_token: chatToken,
      conversation_id: conversationId,
      normalized_occurred_at: nts,
    }

    // Use fetch instead of jQuery for modern approach
    // Add AbortController to handle navigation gracefully
    const controller = new AbortController()

    // Set a timeout to abort the request if it takes too long
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, 5000) // 5 second timeout

    fetch(this.logChatEventPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token':
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content') || '',
      },
      body: JSON.stringify({
        chat_event: params,
      }),
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId)
        return response.json()
      })
      .then((resp) => {
        if (resp.success === false) {
          console.warn('CandidateChatManager - Event logging failed:', resp)
          if (
            resp.chat_event &&
            resp.chat_event.event_type === 'user_logged_out'
          ) {
            console.warn(
              'CandidateChatManager - User logged out, connection may be invalid',
            )
          }
        }
        return resp
      })
      .catch((error) => {
        clearTimeout(timeoutId)

        // Don't log fetch errors if they're due to navigation or abort
        if (error.name === 'AbortError') {
          // Event logging aborted (likely due to navigation)
        } else if (
          error.name === 'TypeError' &&
          error.message.includes('Failed to fetch')
        ) {
          // Event logging failed due to navigation or network issue
        } else {
          console.error('CandidateChatManager - Error logging event:', error)
        }
      })
  }

  // LocalStorage Helper Methods
  // Get chat data from localStorage
  getChatDataFromSession(conversationId) {
    if (!conversationId) {
      return null
    }

    try {
      const storedData = localStorage.getItem(
        `candidate-chat-data-${conversationId}`,
      )
      if (storedData) {
        const parsed = JSON.parse(storedData)
        // Validate that this data belongs to the current conversation
        if (parsed.conversationId === conversationId) {
          return parsed
        } else {
          // Data mismatch - clean it up
          console.warn(
            'CandidateChatManager - Conversation ID mismatch, cleaning up old data',
          )
          this.clearChatData(conversationId)
        }
      }
    } catch (error) {
      console.error(
        'CandidateChatManager - Error reading chat data from localStorage:',
        error,
      )
    }

    // Return default structure if not found
    return {
      events: {},
      messages: [],
      timestamp: null,
      selectedCoach: null,
      chatMode: null,
      conversationId: conversationId,
      callType: null,
      disconnectionReason: null,
      disconnectionTimestamp: null,
    }
  }

  // Save chat data to localStorage (updates existing data)
  saveChatDataToSessionStorage(conversationId, updates) {
    if (!conversationId) {
      console.warn(
        'CandidateChatManager - Cannot save chat data: no conversation ID',
      )
      return
    }

    // Get existing data or create new structure
    const existingData = this.getChatDataFromSession(conversationId)
    const updatedData = {
      ...existingData,
      ...updates,
      conversationId: conversationId,
      timestamp: new Date().toISOString(),
    }

    // Merge events separately to preserve existing event flags
    if (updates.events) {
      updatedData.events = {
        ...existingData.events,
        ...updates.events,
      }
    }

    try {
      localStorage.setItem(
        `candidate-chat-data-${conversationId}`,
        JSON.stringify(updatedData),
      )

      // Clean up old chat data periodically (keep only last 10 conversations)
      this.cleanupOldChatData()
    } catch (error) {
      console.error(
        'CandidateChatManager - Error saving chat data to localStorage:',
        error,
      )
      // If storage is full, try to clean up and retry
      if (error.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded, cleaning up old data...')
        this.cleanupOldChatData(true)
        try {
          localStorage.setItem(
            `candidate-chat-data-${conversationId}`,
            JSON.stringify(updatedData),
          )
        } catch (retryError) {
          console.error(
            'CandidateChatManager - Failed to save after cleanup:',
            retryError,
          )
        }
      }
    }
  }

  // Clear chat data for a specific conversation
  clearChatData(conversationId) {
    if (!conversationId) {
      return
    }

    try {
      localStorage.removeItem(`candidate-chat-data-${conversationId}`)
      localStorage.removeItem(`candidate-missed-call-${conversationId}`)
    } catch (error) {
      console.error('CandidateChatManager - Error clearing chat data:', error)
    }
  }

  // Clean up old chat data (keeps only the most recent conversations)
  cleanupOldChatData(aggressive = false) {
    try {
      const maxConversations = aggressive ? 5 : 10
      const maxAgeDays = aggressive ? 1 : 7

      // Get all candidate chat data keys
      const chatDataKeys = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith('candidate-chat-data-')) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            if (data && data.timestamp) {
              chatDataKeys.push({
                key,
                timestamp: new Date(data.timestamp),
                conversationId: data.conversationId,
              })
            }
          } catch (_e) {
            // Invalid data, remove it
            localStorage.removeItem(key)
          }
        }
      }

      // Sort by timestamp (newest first)
      chatDataKeys.sort((a, b) => b.timestamp - a.timestamp)

      // Remove old conversations
      const now = new Date()
      const maxAge = maxAgeDays * 24 * 60 * 60 * 1000

      chatDataKeys.forEach((item, index) => {
        const age = now - item.timestamp
        const isOld = age > maxAge
        const isBeyondLimit = index >= maxConversations

        if (isOld || isBeyondLimit) {
          /* console.log(
            `CandidateChatManager - Cleaning up old chat data: ${item.conversationId}`,
          ) */
          this.clearChatData(item.conversationId)
        }
      })
    } catch (error) {
      console.error(
        'CandidateChatManager - Error cleaning up old chat data:',
        error,
      )
    }
  }

  // Check if a specific event has been fired
  hasEventFired(eventType, conversationId) {
    if (!conversationId) {
      return false
    }

    const chatData = this.getChatDataFromSession(conversationId)
    return chatData && chatData.events && chatData.events[eventType] === true
  }

  // Mark an event as fired
  markEventFired(eventType, conversationId) {
    if (!conversationId) {
      console.warn(
        `CandidateChatManager - Cannot mark event ${eventType}: no conversation ID`,
      )
      return
    }

    this.saveChatDataToSessionStorage(conversationId, {
      events: {
        [eventType]: true,
      },
    })
  }

  // Build chat history on backend (called when candidate detects disconnection)
  buildChatHistory() {
    // Try to get conversationId from activeCall first, then fallback to lastChatId
    const conversationId =
      this.connectionState.activeCall?.conversationId || this.getLastChatId()

    // Prevent duplicate build chat history calls (only if we have conversationId)
    if (
      conversationId &&
      this.hasEventFired('chat_history_built', conversationId)
    ) {
      /* console.log(
        'CandidateChatManager - Chat history already built, skipping duplicate call',
      ) */
      return
    }
    if (conversationId) {
      this.markEventFired('chat_history_built', conversationId)
    }

    if (!this.buildChatHistoryPath) {
      console.warn(
        'CandidateChatManager - buildChatHistoryPath not set, cannot build chat history',
      )
      return
    }

    if (!conversationId) {
      console.warn(
        'CandidateChatManager - No conversation ID available, cannot build chat history',
      )
      return
    }

    const params = {
      chat_event: {
        candidate_token: this.rtcService?.connectionState?.username,
        conversation_id: this.connectionState.activeCall.conversationId,
      },
    }

    // console.log('CandidateChatManager - Building chat history:', params)

    // Use fetch to post to the build chat history endpoint
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, 5000)

    fetch(this.buildChatHistoryPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token':
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content') || '',
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId)
        return response.json()
      })
      .then((data) => {
        // console.log('CandidateChatManager - Chat history built:', data)
        return data
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        if (error.name === 'AbortError') {
          console.warn(
            'CandidateChatManager - Build chat history request timed out',
          )
        } else {
          console.error(
            'CandidateChatManager - Error building chat history:',
            error,
          )
        }
      })
  }

  // Log missed opportunity function (matches old chat.js pattern)
  logMissedOpportunity(missedType, requestType = null, otherEasyrtcId = null) {
    const missedOpportunityPath =
      this.logChatMissedOpportunityPath ||
      window.log_chat_missed_opportunity_path
    if (!missedOpportunityPath) {
      console.warn(
        'CandidateChatManager - log_chat_missed_opportunity_path not set, cannot log missed opportunity:',
        missedType,
      )
      return
    }

    const chatToken = this._chatToken()
    if (!chatToken) {
      console.warn(
        'CandidateChatManager - chat token not set, cannot log missed opportunity:',
        missedType,
      )
      return
    }

    const ts = new Date().getTime()
    const params = {
      occurred_at: ts,
      missed_type: missedType,
      request_type: requestType,
      chat_token: chatToken,
      other_chat_token: otherEasyrtcId,
    }

    // Use fetch instead of jQuery for modern approach
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, 5000) // 5 second timeout

    fetch(missedOpportunityPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token':
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content') || '',
      },
      body: JSON.stringify({
        chat_missed_opportunity: params,
      }),
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((data) => {
        return data
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        if (error.name !== 'AbortError') {
          console.error(
            'CandidateChatManager - Failed to log missed opportunity:',
            missedType,
            error,
          )
        }
      })
  }

  _setupFrameCommunication() {
    // When in video_chat_frame, parent sets chatFrameLocation (the #chat_browse URL).
    // Initialize currentCandidateUrl so exit/survey redirects go to the right place.
    try {
      if (
        typeof window !== 'undefined' &&
        window.parent &&
        window.parent !== window &&
        window.parent.chatFrameLocation
      ) {
        const ref = window.parent.chatFrameLocation
        window.currentCandidateUrl =
          typeof ref === 'string' ? ref : (ref && ref.pathname) || '/'
      }
    } catch (_e) {
      // Ignore cross-origin or access errors
    }

    // Set up the setFrameLocation function on window object for parent frame communication
    window.setFrameLocation = (url) => {
      // Update the current candidate URL
      window.currentCandidateUrl = url

      // Send navigate action to coach if we have an active call
      if (
        this.connectionState.activeCall &&
        this.connectionState.activeCall.status === 'connected'
      ) {
        this.sendAction({
          action_type: 'navigate',
          action_target: url,
        })
      }
    }

    // Listen for frame location changes from parent window
    window.addEventListener('frameLocationChanged', (event) => {
      const { url } = event.detail

      // Send navigate action to coach if we have an active call
      if (
        this.connectionState.activeCall &&
        this.connectionState.activeCall.status === 'connected'
      ) {
        this.sendAction({
          action_type: 'navigate',
          action_target: url,
        })
      }
    })
  }

  _setupManagerCommunication() {
    // RTC service events
    this.rtcService.addEventListener('connected', (state) => {
      this.connectionState = { ...this.connectionState, ...state }

      // Update MediaService with EasyRTC instance now that connection is established
      const easyrtc = this.rtcService.getEasyRTC()
      if (easyrtc) {
        this.mediaService.setEasyRTC(easyrtc)
      }

      this.notifyListeners('connected', this.connectionState)
      // Also notify stateChange for components that expect it
      this.notifyListeners('stateChange', this.connectionState)
    })

    this.rtcService.addEventListener('disconnected', (state) => {
      this.connectionState = { ...this.connectionState, ...state }
      this.notifyListeners('disconnected', this.connectionState)
      // Also notify stateChange for components that expect it
      this.notifyListeners('stateChange', this.connectionState)
    })

    // Listen to RtcService state changes to keep our connection state in sync
    this.rtcService.addEventListener('stateChange', (state) => {
      // console.log('ChatManager - RtcService stateChange received:', state)
      this.connectionState = { ...this.connectionState, ...state }
      // Notify our listeners with the computed state
      const computedState = this.getConnectionState()
      this.notifyListeners('stateChange', computedState)
    })

    this.rtcService.addEventListener('coachesUpdate', (coaches) => {
      this._handleCoachesUpdate(coaches)
    })

    this.rtcService.addEventListener('candidatesUpdate', (candidates) => {
      this.connectionState.candidates = candidates
      this.notifyListeners('candidatesUpdate', candidates)
    })

    this.rtcService.addEventListener('coachListUpdated', (data) => {
      // Update the coaches in connection state
      this._handleCoachesUpdate(data.coaches)
      this.notifyListeners('coachListUpdated', {
        ...data,
        coaches: this.connectionState.coaches,
      })
    })

    this.rtcService.addEventListener('chatUsersUpdated', (data) => {
      this.notifyListeners('chatUsersUpdated', data)
    })

    this.rtcService.addEventListener('candidateInfoUpdated', (data) => {
      this.notifyListeners('candidateInfoUpdated', data)
    })

    this.rtcService.addEventListener('dataChannelOpen', (data) => {
      this.notifyListeners('dataChannelOpen', data)
    })

    this.rtcService.addEventListener('peerMessage', (data) => {
      // If the message includes a formatted message object, add it to the messages array
      if (data.message) {
        this.addMessage(data.message)
      }

      // Log starting_chat event when we receive a greeting from the coach (candidate-specific)
      if (data.messageType === 'greeting' && this.connectionState.activeCall) {
        this.logEvent(
          'starting_chat',
          {
            coach:
              this.connectionState.selectedCoach?.displayName ||
              'Unknown Coach',
            coach_token: data.fromEasyrtcid || 'unknown',
          },
          this.connectionState.activeCall.conversationId,
        )
      }

      this.notifyListeners('peerMessage', data)
    })

    // Handle when coach ends the chat
    this.rtcService.addEventListener('chatEndedByCoach', (data) => {
      this.handleChatEndedByCoach(data)
    })

    // Handle coach disconnection events
    this.rtcService.addEventListener('coachDisconnected', (data) => {
      this.handleCoachDisconnection(data)
    })

    this.rtcService.addEventListener('coachPeerDisconnected', (data) => {
      this.handleCoachPeerDisconnection(data)
    })

    // When candidate app resumes from background (e.g. phone unlock), check if coach is still connected
    if (typeof document !== 'undefined') {
      this._visibilityHandler = () => {
        if (document.visibilityState !== 'visible') return
        const state = this.getConnectionState()
        if (
          state.chatStatus === 'chat_active' &&
          !state.connectionLost &&
          this.rtcService?.checkCoachConnectionOnResume
        ) {
          this.rtcService.checkCoachConnectionOnResume()
        }
      }
      document.addEventListener('visibilitychange', this._visibilityHandler)
    }

    // Handle typing indicators from coach
    this.rtcService.addEventListener('coachTyping', (data) => {
      this.notifyListeners('coachTyping', data)
    })

    // Route call acceptance/rejection messages from RtcService to CallService
    this.rtcService.addEventListener('callAccepted', (data) => {
      this.callService.handleCallAcceptedByCoach(
        data.fromEasyrtcid,
        data.messageData,
        this.rtcService,
      )
      this.logEvent(
        'call_answer',
        {
          accepted: true,
          callMissed: false,
          coach: this.connectionState.selectedCoach?.displayName,
        },
        this.connectionState.activeCall.conversationId,
      )
    })

    this.rtcService.addEventListener('callRejected', (data) => {
      this.callService.handleCallRejectedByCoach(
        data.fromEasyrtcid,
        data.messageData,
      )
    })

    this.rtcService.addEventListener('callMissed', (data) => {
      this.callService.handleCallMissedByCoach(
        data.fromEasyrtcid,
        data.messageData,
      )
    })

    // Call service events
    this.callService.addEventListener('callInitiated', (data) => {
      this.connectionState.activeCall = data

      // Set lastChatId in sessionStorage for persistence
      if (data.conversationId) {
        this.setLastChatId(data.conversationId)
      }

      this.notifyListeners('callInitiated', data)
      // Also notify stateChange for components that expect it
      const computedState = this.getConnectionState()
      this.notifyListeners('stateChange', computedState)
    })

    this.callService.addEventListener('callRinging', (data) => {
      this.connectionState.activeCall = data
      this.notifyListeners('callRinging', data)
      // Also notify stateChange for components that expect it
      const computedState = this.getConnectionState()
      this.notifyListeners('stateChange', computedState)
    })

    this.callService.addEventListener('callConnected', (data) => {
      this.connectionState.activeCall = data
      // Mark chat as started when call connects (coach has accepted)
      this.connectionState.chatWasStarted = true
      /*
      console.log(
        'ChatManager - Call connected, waiting for EasyRTC peer connection to be ready',
      )
      */
      // console.log('ChatManager - Updated activeCall status:', data.status)

      // Only log call_start event once per call (candidate-specific)
      if (!this.hasEventFired('call_start', data.conversationId)) {
        this.logEvent(
          'call_start',
          {
            candidate: this.connectionState.username || 'unknown',
            call_mode: data.callType || this.connectionState.chatMode,
            candidate_info: {
              easyrtcid: this.connectionState.myEasyrtcid || 'unknown',
              displayName:
                this.connectionState.selectedCoach?.displayName ||
                'Unknown Coach',
            },
          },
          data.conversationId,
        )
        this.markEventFired('call_start', data.conversationId)
      }

      this.notifyListeners('callConnected', data)

      // Only send "started_chat_from" action once per call (legacy compatibility)
      if (!this.hasEventFired('started_chat_from', data.conversationId)) {
        const chatFrameLocation =
          window.parent?.chatFrameLocation || window.location
        let actionTarget

        if (
          chatFrameLocation.hostname &&
          chatFrameLocation.hostname.includes('.com')
        ) {
          actionTarget = chatFrameLocation.pathname
        } else {
          actionTarget = chatFrameLocation.pathname
        }

        this.sendAction({
          action_type: 'started_chat_from',
          action_target: actionTarget || '/',
        })
        this.markEventFired('started_chat_from', data.conversationId)
      }

      // If this is a video/audio call, stream will be sent when media is ready
      if (data.callType === 'video' || data.callType === 'audio') {
        /*
        console.log(
          'ChatManager - Call connected, stream will be sent when media is ready',
        )
        */
      }

      // Also notify stateChange for components that expect it
      // Use getConnectionState() to ensure the computed state is passed
      const computedState = this.getConnectionState()
      /*
      console.log(
        'ChatManager - Computed state after call connected:',
        computedState,
      )
      */
      this.notifyListeners('stateChange', computedState)
    })

    this.callService.addEventListener('callRejected', (data) => {
      // Treat rejection as missed call - candidate doesn't need to know they were rejected
      // Log as missed call
      this.logEvent(
        'call_answer',
        {
          accepted: false,
          callMissed: true,
          coach: this.connectionState.selectedCoach?.displayName,
        },
        this.connectionState.activeCall?.conversationId,
      )

      // Store missed call data before clearing activeCall (treating rejection as missed)
      if (this.connectionState.activeCall) {
        const missedCallData = {
          coach: this.connectionState.selectedCoach,
          callType: this.connectionState.activeCall.callType,
          conversationId: this.connectionState.activeCall.conversationId,
          timestamp: new Date().toISOString(),
          reason: 'missed', // Store as missed, not rejected
        }

        // Store in localStorage for the MissedCall component
        try {
          localStorage.setItem(
            `candidate-missed-call-${this.connectionState.activeCall.conversationId}`,
            JSON.stringify(missedCallData),
          )
        } catch (error) {
          console.error('Error storing missed call data:', error)
        }
      }

      const missedConversationId =
        this.connectionState.activeCall?.conversationId

      // Set chatStatus to call_missed (treating rejection as missed)
      this.connectionState.chatStatus = 'call_missed'
      this.connectionState.activeCall = null
      this._releaseChatMedia(missedConversationId)
      this.notifyListeners('callMissed', data)

      // Also notify stateChange for components that expect it
      this.notifyListeners('stateChange', this.connectionState)
    })

    this.callService.addEventListener('callMissed', (data) => {
      this.logEvent(
        'call_answer',
        {
          accepted: false,
          callMissed: true,
          coach: this.connectionState.selectedCoach?.displayName,
        },
        this.connectionState.activeCall.conversationId,
      )

      // Store missed call data before clearing activeCall
      if (this.connectionState.activeCall) {
        const missedCallData = {
          coach: this.connectionState.selectedCoach,
          callType: this.connectionState.activeCall.callType,
          conversationId: this.connectionState.activeCall.conversationId,
          timestamp: new Date().toISOString(),
          reason: 'missed',
        }
        // console.log(
        //   'CandidateChatManager - storing missed call data:',
        //   missedCallData,
        // )

        // Store in localStorage for the MissedCall component
        try {
          localStorage.setItem(
            `candidate-missed-call-${this.connectionState.activeCall.conversationId}`,
            JSON.stringify(missedCallData),
          )
          // console.log(
          //   'CandidateChatManager - stored missed call data in localStorage',
          // )
        } catch (error) {
          console.error('Error storing missed call data:', error)
        }
      } else {
        /* console.log(
          'CandidateChatManager - no activeCall to store missed call data for',
        ) */
      }

      const missedConversationId =
        this.connectionState.activeCall?.conversationId

      // Set chatStatus to call_missed before clearing activeCall
      this.connectionState.chatStatus = 'call_missed'
      this.connectionState.activeCall = null
      this._releaseChatMedia(missedConversationId)
      this.notifyListeners('callMissed', data)
      // Also notify stateChange for components that expect it
      this.notifyListeners('stateChange', this.connectionState)
    })

    this.callService.addEventListener('callEnded', (data) => {
      this.connectionState.activeCall = null
      this.notifyListeners('callEnded', data)
      // Also notify stateChange for components that expect it
      this.notifyListeners('stateChange', this.connectionState)
    })

    // Handle local media ready events from CallService
    this.callService.addEventListener('localVideoReady', (data) => {
      this.connectionState.hasLocalVideo = data.hasLocalVideo
      this.connectionState.hasLocalAudio = data.hasLocalAudio
      this.connectionState.localStream = data.stream

      // Update MediaService with the stream from EasyRTC
      if (data.stream) {
        this.mediaService.setLocalStream(data.stream)
        this.logEvent(
          'video_success',
          { early: true },
          this.connectionState.activeCall.conversationId,
        )
      } else {
        this.logEvent(
          'video_failure',
          { errorCode: 'no_stream', errorDesc: 'No video stream available' },
          this.connectionState.activeCall.conversationId,
        )
      }

      // Notify listeners about the state change
      this.notifyListeners('stateChange', this.connectionState)
      this.notifyListeners('localVideoReady', data)
    })

    this.callService.addEventListener('localAudioReady', (data) => {
      this.connectionState.hasLocalVideo = data.hasLocalVideo
      this.connectionState.hasLocalAudio = data.hasLocalAudio
      this.connectionState.localStream = data.stream

      // Update MediaService with the stream from EasyRTC
      if (data.stream) {
        this.mediaService.setLocalStream(data.stream)
        this.logEvent(
          'audio_success',
          { early: true },
          this.connectionState.activeCall.conversationId,
        )
      } else {
        this.logEvent(
          'audio_failure',
          { errorCode: 'no_stream', errorDesc: 'No audio stream available' },
          this.connectionState.activeCall.conversationId,
        )
      }

      // Notify listeners about the state change
      this.notifyListeners('stateChange', this.connectionState)
      this.notifyListeners('localAudioReady', data)
    })

    // Video loading state events
    this.callService.addEventListener('videoLoadingStarted', (data) => {
      // console.log('ChatManager - Video loading started:', data)
      // Set chat status to indicate video is loading
      this.connectionState.chatStatus = 'obtaining_video'
      this.notifyListeners('videoLoadingStarted', data)
      // Also notify stateChange for components that expect it
      this.notifyListeners('stateChange', this.connectionState)
    })

    // Media service events
    this.mediaService.addEventListener('mediaSourceInitialized', (data) => {
      // console.log('ChatManager - Media source initialized:', data)
      // Update chat status to indicate media is ready
      if (this.connectionState.chatStatus === 'obtaining_video') {
        this.connectionState.chatStatus = 'waiting_for_coach'
      }

      // If we have an active call and local stream, send it immediately
      // This matches the classic system behavior where addStreamToCall happens
      // during media initialization, not after call acceptance
      if (
        this.connectionState.activeCall &&
        this.mediaService.getLocalStream()
      ) {
        this.sendLocalStreamToCoach(
          this.connectionState.activeCall.coachEasyrtcid,
          this.connectionState.activeCall.conversationId,
        )
      }

      this.notifyListeners('mediaSourceInitialized', data)
      // Also notify stateChange for components that expect it
      this.notifyListeners('stateChange', this.connectionState)
    })

    this.mediaService.addEventListener('mediaSourceInitFailed', (data) => {
      console.error('ChatManager - Media source initialization failed:', data)
      // Update chat status to indicate media failed
      this.connectionState.chatStatus = 'media_failed'

      // Log media failure events based on chat mode
      if (this.connectionState.activeCall) {
        if (this.connectionState.chatMode === 'video') {
          this.logEvent(
            'video_failure',
            {
              errorCode: data.errorCode || 'unknown',
              errorDesc: data.errorDesc || 'Media initialization failed',
            },
            this.connectionState.activeCall.conversationId,
          )
        } else if (this.connectionState.chatMode === 'audio') {
          this.logEvent(
            'video_failure',
            {
              errorCode: data.errorCode || 'unknown',
              errorDesc: data.errorDesc || 'Media initialization failed',
            },
            this.connectionState.activeCall.conversationId,
          )
        }

        // Send video_failed message to coach to notify them of media failure
        this.sendVideoFailedMessage(data)
      }

      this.notifyListeners('mediaSourceInitFailed', data)
      // Also notify stateChange for components that expect it
      this.notifyListeners('stateChange', this.connectionState)
    })

    this.rtcService.addEventListener('remoteStreamReceived', (data) => {
      // console.log('ChatManager - Remote stream received from RtcService:', data)

      // Update video/audio connection state separately from chat status
      this.connectionState.hasRemoteVideo = data.hasVideo
      this.connectionState.hasRemoteAudio = data.hasAudio
      this.connectionState.remoteStream = data.stream

      // Keep chatStatus for general chat state, not video connection state
      // Only update chatStatus if we don't already have a meaningful status
      if (
        !this.connectionState.chatStatus ||
        this.connectionState.chatStatus === 'obtaining_video' ||
        this.connectionState.chatStatus === 'obtaining_audio'
      ) {
        this.connectionState.chatStatus = 'connected'
      }

      // Notify listeners about the state change
      this.notifyListeners('stateChange', this.connectionState)
      this.notifyListeners('remoteStreamReceived', data)

      // console.log('ChatManager - Connection state updated for remote stream')
    })

    this.rtcService.addEventListener('connectionStateChanged', (_data) => {
      // Handle connection state changes from RTC service
    })
  }

  /**
   * Merge cached coach-info onto room occupant rows, notify listeners, and
   * fetch any missing profiles in the background.
   *
   * @param {Array<Object>} coaches
   * @private
   */
  _handleCoachesUpdate(coaches) {
    const list = Array.isArray(coaches) ? coaches : []
    const merged = this.mergeCachedCoachInfo(list)
    this.connectionState.coaches = merged
    this.notifyListeners('coachesUpdate', merged)
    this.fetchMissingCoachInfos(merged)
  }

  /**
   * Apply coach-info expand payload onto a coach occupant.
   * Room fields own list identity (name/avatar); API owns skills/accolades/bio.
   *
   * @param {Object} coach
   * @param {Object} info
   * @returns {Object}
   */
  mergeCoachInfoOntoCoach(coach, info) {
    if (!coach || !info) return coach
    return {
      ...coach,
      coachId: info.id ?? coach.coachId,
      coachSkills: info.skills || coach.coachSkills || [],
      coachAccolades: info.accolades || coach.coachAccolades,
      coachBio: info.bio ?? coach.coachBio,
      hasCoachVideo: info.hasCoachVideo ?? coach.hasCoachVideo,
      coachVideo: info.coachVideo ?? coach.coachVideo,
      coachInfoLoaded: true,
    }
  }

  /**
   * Sync-merge any already-cached coach-info onto the coaches list.
   *
   * @param {Array<Object>} coaches
   * @returns {Array<Object>}
   */
  mergeCachedCoachInfo(coaches) {
    return (coaches || []).map((coach) => {
      const id = coach.coachId
      if (!id || !this.coachInfo[id]) return coach
      return this.mergeCoachInfoOntoCoach(coach, this.coachInfo[id])
    })
  }

  /**
   * Fetch coach-info for any coaches missing from the cache, then re-notify.
   *
   * @param {Array<Object>} coaches
   */
  async fetchMissingCoachInfos(coaches) {
    const ids = [
      ...new Set(
        (coaches || [])
          .map((c) => c.coachId)
          .filter((id) => id && !this.coachInfo[id]),
      ),
    ]
    if (ids.length === 0) return

    await Promise.all(ids.map((id) => this.fetchCoachInfo(id)))
    const refreshed = this.mergeCachedCoachInfo(
      this.connectionState.coaches || coaches,
    )
    this.connectionState.coaches = refreshed
    this.notifyListeners('coachesUpdate', refreshed)
  }

  /**
   * Fetch a single coach profile from GET /coach-info (cached).
   *
   * @param {number|string} coachId
   * @returns {Promise<Object|null>}
   */
  async fetchCoachInfo(coachId) {
    if (!coachId || !this.chatCoachInfoPath) {
      return null
    }

    if (this.coachInfo[coachId]) {
      return this.coachInfo[coachId]
    }

    if (this._coachInfoInflight[coachId]) {
      return this._coachInfoInflight[coachId]
    }

    this._coachInfoInflight[coachId] = (async () => {
      try {
        const url = new URL(this.chatCoachInfoPath, window.location.origin)
        url.searchParams.set('id', String(coachId))

        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        })

        if (!response.ok) {
          console.warn(
            'CandidateChatManager - coach-info request failed:',
            response.status,
            coachId,
          )
          return null
        }

        const data = await response.json()
        const info = data?.coach
        if (info?.id) {
          this.coachInfo[info.id] = info
        }
        return info || null
      } catch (error) {
        console.error(
          'CandidateChatManager - Error fetching coach-info:',
          error,
        )
        return null
      } finally {
        delete this._coachInfoInflight[coachId]
      }
    })()

    return this._coachInfoInflight[coachId]
  }

  // Main connection method
  async connect(socketUrl, username, chatRoom, options = {}) {
    // Don't reconnect on survey / missed-call routes (CC-UX-027)
    const urlParams = new URLSearchParams(window.location.search)
    if (
      urlParams.get('chat_ended') === '1' ||
      urlParams.get('call_missed') === '1'
    ) {
      return this.connectionState
    }

    // Set logging properties from options (only if provided)
    if (options.logChatEventPath !== undefined) {
      this.logChatEventPath = options.logChatEventPath
    }
    if (options.logChatMissedOpportunityPath !== undefined) {
      this.logChatMissedOpportunityPath = options.logChatMissedOpportunityPath
    }
    if (options.buildChatHistoryPath !== undefined) {
      this.buildChatHistoryPath = options.buildChatHistoryPath
    }
    if (options.chatCoachInfoPath !== undefined) {
      this.chatCoachInfoPath = options.chatCoachInfoPath
    }
    if (options.timeOffset !== undefined) {
      this.serverTimeOffset = options.timeOffset
    }

    // Pass logging configuration to RtcService
    if (this.logChatEventPath) {
      this.rtcService.setLoggingConfig(
        this.logChatEventPath,
        this.serverTimeOffset,
      )
    }

    // Debug logging path

    // Set user mode in options if not already set
    if (!options.userMode) {
      options.userMode = 'coach' // Default to coach mode
      /*
      console.log(
        'ChatManager - No userMode provided, defaulting to:',
        options.userMode,
      )
      */
    } else {
      // console.log('ChatManager - Using provided userMode:', options.userMode)
    }

    // If we're connecting with a different userMode, reset the connection state
    if (
      this.connectionState.userMode &&
      this.connectionState.userMode !== options.userMode
    ) {
      // Disconnect from previous connection if connected
      if (this.connectionState.isConnected) {
        this.rtcService.disconnect()
        this.callService.cleanup()
      }

      this.connectionState = {
        ...this.connectionState,
        isConnected: false,
        coaches: [],
        candidates: [],
        selectedCoach: null,
        activeCall: null,
        chatRoom: null,
        myEasyrtcid: null,
        messages: [],
        chatMode: 'text',
        chatStatus: 'disconnected',
        hasLocalVideo: false,
        hasLocalAudio: false,
        hasRemoteVideo: false,
        hasRemoteAudio: false,
        localStream: null,
        remoteStream: null,
        userMode: options.userMode, // Update userMode
      }
    }

    return this.rtcService
      .connect(socketUrl, username, chatRoom, options)
      .then((connectionState) => {
        // Frame owns presence as soon as it joins the room (not when coach answers)
        if (options.userMode === 'candidate') {
          this.rtcService.completeCandidateChatHandoff(
            options.conversationId ||
              this.connectionState.activeCall?.conversationId,
          )
        }
        return connectionState
      })
  }

  /**
   * Soft-handoff from coach list → chat frame: Starting Chat without leaving the room.
   *
   * @param {string} [conversationId]
   */
  beginChatHandoff(conversationId) {
    markChatHandoffNavigation()
    if (this.rtcService) {
      this.rtcService.beginCandidateChatHandoff(conversationId)
    }
  }

  // Handle degraded mode fallback
  handleDegradedModeFallback(requestedCallType) {
    // If MediaService is in degraded mode and user requested video/audio, fallback to text
    if (
      this.mediaService.isDegradedMode() &&
      (requestedCallType === 'video' || requestedCallType === 'audio')
    ) {
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      )

      // Show user-friendly message
      let message = 'Video/audio chat is not available. Switching to text chat.'

      if (isSafari) {
        if (!window.isSecureContext) {
          message =
            'Safari requires HTTPS for video/audio chat. Switching to text chat.'
        } else {
          message =
            'Video/audio chat may require additional permissions in Safari. Switching to text chat.'
        }
      }

      // Notify listeners about the fallback
      this.notifyListeners('mediaDegradedMode', {
        requestedCallType,
        actualCallType: 'text',
        message,
        isDegradedMode: true,
      })

      console.warn(`ChatManager - ${message}`)
      return 'text'
    }

    return requestedCallType
  }

  // Call management methods
  async initiateCall(coachEasyrtcid, callType = 'text', conversationId = null) {
    // Don't initiate call if chat has ended or call was missed
    const urlParams = new URLSearchParams(window.location.search)
    const isChatEndedFromUrl =
      urlParams.get('chat_ended') === '1' ||
      urlParams.get('call_missed') === '1'
    const chatStatus = this.connectionState.chatStatus

    if (
      isChatEndedFromUrl ||
      chatStatus === 'chat_ended' ||
      chatStatus === 'call_missed'
    ) {
      // console.log(
      //   'CandidateChatManager - Cannot initiate call: chat has ended or call was missed',
      //   { isChatEndedFromUrl, chatStatus },
      // )
      return
    }

    // Check for degraded mode and fallback to text if needed
    const actualCallType = this.handleDegradedModeFallback(callType)

    // Set the chat mode in the connection state
    this.connectionState.chatMode = actualCallType
    this.connectionState.chatStatus = 'initiating_call'

    // Check if this is a new conversation or reconnection to existing conversation
    const currentConversationId =
      this.connectionState.activeCall?.conversationId
    const isNewConversation =
      conversationId && conversationId !== currentConversationId

    // If starting a new conversation, clear messages from previous conversation
    if (isNewConversation) {
      // console.log(
      //   'CandidateChatManager - Starting new conversation, clearing previous messages',
      //   { old: currentConversationId, new: conversationId },
      // )
      this.connectionState.messages = []

      // Clear old conversation data to prevent conflicts
      if (currentConversationId) {
        // console.log(
        //   'CandidateChatManager - Clearing old conversation data:',
        //   currentConversationId,
        // )
        this.clearChatData(currentConversationId)
      }
    }

    // Load persisted data for this conversation if available (handles reconnection)
    if (conversationId) {
      const chatData = this.getChatDataFromSession(conversationId)
      // Validate that the loaded data belongs to this conversation
      if (chatData && chatData.conversationId === conversationId) {
        // console.log(
        //   'CandidateChatManager - Loading persisted data for conversation:',
        //   conversationId,
        // )

        // Restore messages if available
        if (chatData.messages && chatData.messages.length > 0) {
          this.connectionState.messages = chatData.messages
        }

        // Restore selectedCoach if available
        if (chatData.selectedCoach) {
          this.connectionState.selectedCoach = chatData.selectedCoach
        }
      } else if (chatData && chatData.conversationId !== conversationId) {
        // Data mismatch - clear it
        console.warn(
          'CandidateChatManager - Conversation ID mismatch, clearing invalid data',
          { expected: conversationId, found: chatData.conversationId },
        )
        this.clearChatData(conversationId)
      }
    }

    // Save callType and other initial data to localStorage immediately
    // Also clear any old data for this conversation to prevent conflicts
    if (conversationId) {
      // Clear old data for this conversation to ensure clean state
      this.clearChatData(conversationId)
      this.saveChatDataToSessionStorage(conversationId, {
        callType: actualCallType,
        chatMode: actualCallType,
        selectedCoach: this.connectionState.selectedCoach,
      })
    }

    // Log initiating_chat event (candidate-specific)
    this.logEvent(
      'initiating_chat',
      {
        coach:
          this.connectionState.selectedCoach?.displayName || 'Unknown Coach',
        coach_token: coachEasyrtcid,
        mode: actualCallType,
        requested_mode: callType, // Log the original requested mode
      },
      conversationId,
    )

    // Update presence to show "Starting Chat"
    if (this.rtcService) {
      this.rtcService.syncCandidateCallRoomFields({
        chatMode: actualCallType,
        conversationId:
          conversationId || this.connectionState.activeCall?.conversationId,
      })
      this.rtcService.updateCandidatePresenceStartingChat(conversationId)
    }

    // ⚠️ CORRECTED: No media initialization here - follow legacy pattern
    // Media will be initialized in CallService.performCall() right before easyrtc.call()
    // This ensures we use the same easyrtc instance and only initialize when needed

    // Now initiate the call (media will be initialized later when accepted)
    try {
      return this.callService.initiateCall(
        coachEasyrtcid,
        actualCallType,
        this.rtcService,
        conversationId,
      )
    } catch (error) {
      // CTS-9831: Coach went inactive between list click and call_start
      if (error?.code === 'COACH_UNAVAILABLE') {
        console.warn(
          'CandidateChatManager - Aborting call; coach is unavailable/away',
          { coachEasyrtcid },
        )
        this.connectionState.chatStatus = 'call_missed'
        this.connectionState.activeCall = null
        const computedState = this.getConnectionState()
        this.notifyListeners('stateChange', computedState)
        this.notifyListeners('callMissed', {
          reason: 'coach_unavailable',
          coachEasyrtcid,
        })
        return null
      }
      throw error
    }
  }

  // Manual event triggers for events that components expect but aren't part of the core flow
  triggerIncomingCall(data) {
    this.notifyListeners('incomingCall', data)
  }

  // Utility methods
  disconnect() {
    this.rtcService.disconnect()
    this.callService.cleanup()
    this.connectionState.isConnected = false
    this.notifyListeners('disconnected', this.connectionState)
  }

  getConnectionState() {
    // Compute chatStatus based on current state
    const state = { ...this.connectionState }

    // If chat has already ended or call was missed, preserve that status
    if (state.chatStatus === 'chat_ended') {
      // Keep the chat_ended status even when disconnected
    } else if (state.chatStatus === 'call_missed') {
      // Keep the call_missed status even when activeCall is cleared
    } else if (state.chatStatus === 'call_cancelled') {
      // Keep the call_cancelled status even when activeCall is cleared
    } else if (!state.isConnected) {
      state.chatStatus = 'disconnected'

      // Stop heartbeat monitoring when disconnected
      if (this.rtcService && this.rtcService.stopHeartbeatMonitoring) {
        this.rtcService.stopHeartbeatMonitoring()
      }
    } else if (!state.activeCall) {
      state.chatStatus = 'connecting'

      // Stop heartbeat monitoring when no active call
      if (this.rtcService && this.rtcService.stopHeartbeatMonitoring) {
        this.rtcService.stopHeartbeatMonitoring()
      }
    } else if (
      state.activeCall.status === 'initiating' ||
      state.activeCall.status === 'ringing'
    ) {
      state.chatStatus = 'waiting_for_coach'
    } else if (state.activeCall.status === 'connected') {
      state.chatStatus = 'chat_active'

      // Start heartbeat monitoring when chat becomes active
      if (this.rtcService && this.rtcService.startHeartbeatMonitoring) {
        this.rtcService.startHeartbeatMonitoring()
      }
    } else if (state.activeCall.status === 'missed') {
      state.chatStatus = 'call_missed'

      // Stop heartbeat monitoring when call is missed
      if (this.rtcService && this.rtcService.stopHeartbeatMonitoring) {
        this.rtcService.stopHeartbeatMonitoring()
      }
    } else if (
      state.activeCall.status === 'ended' ||
      state.activeCall.status === 'failed' ||
      state.activeCall.status === 'rejected'
    ) {
      state.chatStatus = 'chat_ended'

      // Stop heartbeat monitoring when chat ends
      if (this.rtcService && this.rtcService.stopHeartbeatMonitoring) {
        this.rtcService.stopHeartbeatMonitoring()
      }
    } else {
      state.chatStatus = 'unknown'

      // Stop heartbeat monitoring for unknown status
      if (this.rtcService && this.rtcService.stopHeartbeatMonitoring) {
        this.rtcService.stopHeartbeatMonitoring()
      }
    }

    // Include media state information
    const mediaState = this.mediaService.getMediaState()
    // Keep the hasLocalVideo/hasLocalAudio values that were set by CallService events
    // Don't override them with MediaService values as they are more reliable
    state.localStream = this.mediaService.localStream
    // Don't override remoteStream - it's managed by RtcService, not MediaService

    // Include media control states for UI consistency
    state.videoPaused = mediaState.videoPaused
    state.audioMuted = mediaState.audioMuted

    /*
    console.log(
      'ChatManager - getConnectionState computed chatStatus:',
      state.chatStatus,
    )
    */
    return state
  }

  // Add method to add messages
  addMessage(message) {
    // console.log('CandidateChatManager.addMessage called with:', message)
    // Create a new array reference so React can detect the change
    this.connectionState.messages = [...this.connectionState.messages, message]
    // console.log('Messages array after adding:', this.connectionState.messages)

    // Save messages to localStorage
    const conversationId =
      this.connectionState.activeCall?.conversationId || this.getLastChatId()
    if (conversationId) {
      this.saveChatDataToSessionStorage(conversationId, {
        messages: this.connectionState.messages,
      })
    }

    this.notifyListeners('messageAdded', message)
    this.notifyListeners('stateChange', this.getConnectionState())
  }

  // Add method to update selectedCoach in connection state
  setSelectedCoach(coach) {
    this.connectionState.selectedCoach = coach

    // Save selectedCoach to localStorage
    const conversationId =
      this.connectionState.activeCall?.conversationId || this.getLastChatId()
    if (conversationId) {
      this.saveChatDataToSessionStorage(conversationId, {
        selectedCoach: coach,
      })
    }

    this.notifyListeners('selectedCoachUpdated', coach)
    this.notifyListeners('stateChange', this.getConnectionState())
  }

  // Add method to set connection lost state
  setConnectionLost(lost = true) {
    this.connectionState.connectionLost = lost
    this.notifyListeners('connectionLost', { connectionLost: lost })
    this.notifyListeners('stateChange', this.getConnectionState())
  }

  // Add method to set user type
  setUserType(userType) {
    this.connectionState.deloitteUser = userType === 'deloitte'
    this.notifyListeners('userTypeUpdated', {
      deloitteUser: this.connectionState.deloitteUser,
    })
    this.notifyListeners('stateChange', this.getConnectionState())
  }

  // Add method to send messages
  sendMessage(content) {
    if (
      !this.connectionState.activeCall ||
      this.connectionState.activeCall.status !== 'connected'
    ) {
      throw new Error('No active chat connection')
    }

    const message = {
      id: Date.now(),
      type: 'text',
      content,
      timestamp: new Date().toISOString(),
      sender: 'candidate',
    }

    // Add message to local messages array
    this.addMessage(message)

    // Log text_message event (candidate-specific)
    this.logEvent(
      'text_message',
      {
        who: 'candidate',
        text: content,
      },
      this.connectionState.activeCall.conversationId,
    )

    // Send message via EasyRTC using "message" type (legacy compatibility)
    const easyrtc = this.rtcService.getEasyRTC()
    if (easyrtc) {
      easyrtc.sendData(
        this.connectionState.activeCall.coachEasyrtcid,
        'message', // Use "message" type for legacy compatibility
        content,
      )
    }

    return message
  }

  // Send video_failed message to coach when media fails (legacy compatibility)
  sendVideoFailedMessage(data) {
    if (
      !this.connectionState.activeCall ||
      !this.connectionState.activeCall.coachEasyrtcid
    ) {
      console.warn(
        'CandidateChatManager - Cannot send video_failed message: no active call or coach ID',
      )
      return
    }

    const easyrtc = this.rtcService.getEasyRTC()
    if (easyrtc) {
      easyrtc.sendData(
        this.connectionState.activeCall.coachEasyrtcid,
        'video_failed', // Use "video_failed" type for legacy compatibility
        data,
        undefined,
        this.connectionState.activeCall.conversationId,
      )
    }
  }

  // Add method to send candidate actions to coach (legacy compatibility)
  sendAction(action) {
    if (!action || !action.action_type) {
      console.warn(
        'CandidateChatManager - sendAction called with invalid action:',
        action,
      )
      return
    }

    if (
      !this.connectionState.activeCall ||
      this.connectionState.activeCall.status !== 'connected'
    ) {
      console.warn(
        'CandidateChatManager - No active chat connection, cannot send action',
      )
      return
    }

    // Log candidate_action event (candidate-specific)
    this.logEvent(
      'candidate_action',
      {
        who: 'candidate',
        easyrtcId: this.connectionState.myEasyrtcid,
        action: action.action_type,
        action_target: action.action_target,
        paused: action.paused,
      },
      this.connectionState.activeCall.conversationId,
    )

    // Send action to coach via EasyRTC using "candidate_action" type
    const easyrtc = this.rtcService.getEasyRTC()
    if (easyrtc) {
      easyrtc.sendData(
        this.connectionState.activeCall.coachEasyrtcid,
        'candidate_action', // Use "candidate_action" type for legacy compatibility
        action,
      )
    }

    return action
  }

  // Add method to send typing indicators (legacy compatibility)
  sendTypingIndicator() {
    // Don't send typing indicators for Deloitte users
    // Based on original chat.js pattern from lines 1918-1921
    if (this.connectionState.deloitteUser === true) {
      return
    }

    if (
      !this.connectionState.activeCall ||
      this.connectionState.activeCall.status !== 'connected'
    ) {
      return
    }

    const easyrtc = this.rtcService.getEasyRTC()
    if (easyrtc) {
      easyrtc.sendData(
        this.connectionState.activeCall.coachEasyrtcid,
        'typing', // Use "typing" type for legacy compatibility
        '',
      )
    }
  }

  // Event listener management
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event).add(callback)
  }

  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback)
    }
  }

  notifyListeners(event, data) {
    /*
     */
    // console.log(`CandidateChatManager - notifyListeners data:`, data)
    /*
    console.log(
      `CandidateChatManager - notifyListeners has ${this.listeners.size} total listeners`,
    )
    */

    if (this.listeners.has(event)) {
      const eventListeners = this.listeners.get(event)
      /*
      console.log(
        `CandidateChatManager - notifyListeners found ${eventListeners.size} listeners for event: ${event}`,
      )
      */

      eventListeners.forEach((callback, _index) => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in ${event} listener:`, error)
        }
      })
    } else {
      /*
      console.log(
        `CandidateChatManager - notifyListeners no listeners found for event: ${event}`,
      )
      */
    }
  }

  // Coach management methods
  getCoachesByStatus(coaches, status) {
    return coaches.filter((coach) => coach.status === status)
  }

  getAvailableCoaches(coaches) {
    return applyCoachLoadBalancing(this.getCoachesByStatus(coaches, 'chat'))
  }

  getBusyCoaches(coaches) {
    return this.getCoachesByStatus(coaches, 'dnd')
  }

  selectFirstAvailableCoach(coaches) {
    const availableCoaches = this.getAvailableCoaches(coaches)
    return availableCoaches.length > 0 ? availableCoaches[0] : null
  }

  filterCoachesByLocale(coaches, locale) {
    return coaches.filter((coach) => {
      if (!coach.coachLocales) return true
      return coach.coachLocales.indexOf(locale) !== -1
    })
  }

  getCurrentCandidates() {
    /*
    console.log(
      'ChatManager - getCurrentCandidates called, returning:',
      this.connectionState.candidates,
    )
    */
    return this.connectionState.candidates
  }

  // Force refresh room occupants to get latest coaches and candidates
  forceRefreshRoomOccupants() {
    if (this.rtcService) {
      this.rtcService.forceRefreshRoomOccupants()
    }
  }

  // Get current coaches and candidates
  getCurrentCoaches() {
    return this.connectionState.coaches
  }

  // Media service initialization
  async _initializeMediaService() {
    try {
      const initialized = await this.mediaService.initialize()
      if (initialized) {
        this._setupMediaServiceEvents()

        // Set EasyRTC instance in MediaService for enableMicrophone calls
        const easyrtc = this.rtcService.getEasyRTC()
        if (easyrtc) {
          this.mediaService.setEasyRTC(easyrtc)
        }

        // Check if MediaService is in degraded mode
        if (this.mediaService.isDegradedMode()) {
          const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent,
          )

          if (isSafari) {
            // For Safari, check if it's due to HTTPS requirement
            if (!window.isSecureContext) {
              console.warn(
                'ChatManager - Safari requires HTTPS for media access. Please use a secure connection.',
              )
            } else {
              console.warn(
                'ChatManager - Safari media access may require user permission. Video chat will work once permissions are granted.',
              )
            }
          } else {
            console.warn(
              'ChatManager - MediaService is running in degraded mode (media not supported)',
            )
          }

          this.notifyListeners(
            'mediaDegradedMode',
            this.mediaService.getServiceStatus(),
          )
        }
      } else {
        console.warn('ChatManager - MediaService initialization failed')
      }
    } catch (error) {
      console.error('ChatManager - MediaService initialization error:', error)
    }
  }

  // Set up media service event handling
  _setupMediaServiceEvents() {
    // Media service events
    this.mediaService.addEventListener('initialized', (mediaState) => {
      this.notifyListeners('mediaInitialized', mediaState)
    })

    this.mediaService.addEventListener('initializationFailed', (data) => {
      this.notifyListeners('mediaInitializationFailed', data)
    })

    this.mediaService.addEventListener('permissionsUpdated', (permissions) => {
      this.notifyListeners('mediaPermissionsUpdated', permissions)
    })

    this.mediaService.addEventListener('devicesUpdated', (devices) => {
      this.notifyListeners('mediaDevicesUpdated', devices)
    })

    this.mediaService.addEventListener('mediaAccessGranted', (data) => {
      this.notifyListeners('mediaAccessGranted', data)
    })

    this.mediaService.addEventListener('permissionDenied', (data) => {
      this.notifyListeners('mediaPermissionDenied', data)
    })

    this.mediaService.addEventListener('deviceNotFound', (data) => {
      this.notifyListeners('mediaDeviceNotFound', data)
    })

    this.mediaService.addEventListener('deviceInUse', (data) => {
      this.notifyListeners('mediaDeviceInUse', data)
    })

    this.mediaService.addEventListener('mediaSourceInitialized', (data) => {
      this.notifyListeners('mediaSourceInitialized', data)
    })

    this.mediaService.addEventListener('mediaSourceInitFailed', (data) => {
      this.notifyListeners('mediaSourceInitFailed', data)
    })

    this.mediaService.addEventListener('selfVideoReady', (data) => {
      this.notifyListeners('selfVideoReady', data)
    })

    this.mediaService.addEventListener('audioReady', (data) => {
      this.notifyListeners('audioReady', data)
    })

    this.mediaService.addEventListener('audioVisualizerReady', (data) => {
      this.notifyListeners('audioVisualizerReady', data)
    })

    this.mediaService.addEventListener('audioControlsReady', (data) => {
      this.notifyListeners('audioControlsReady', data)
    })

    this.mediaService.addEventListener('audioMuteChanged', (data) => {
      // Update connection state with the new audio mute state
      this.connectionState.audioMuted = data.muted
      this.notifyListeners('audioMuteChanged', data)
      // Also notify stateChange to update UI components
      this.notifyListeners('stateChange', this.getConnectionState())
    })

    this.mediaService.addEventListener('videoPauseChanged', (data) => {
      // Update connection state with the new video pause state
      this.connectionState.videoPaused = data.paused
      this.notifyListeners('videoPauseChanged', data)
      // Also notify stateChange to update UI components
      this.notifyListeners('stateChange', this.getConnectionState())
    })

    this.mediaService.addEventListener('sendDataChannelMessage', (data) => {
      this.sendDataChannelMessage(data.messageType, data.messageData)
    })

    this.mediaService.addEventListener('audioDeviceChanged', (data) => {
      this.notifyListeners('audioDeviceChanged', data)
    })

    this.mediaService.addEventListener('videoDeviceChanged', (data) => {
      this.notifyListeners('videoDeviceChanged', data)
    })

    this.mediaService.addEventListener('streamRestarted', (data) => {
      this.notifyListeners('streamRestarted', data)
    })

    this.mediaService.addEventListener('remoteVideoReady', (data) => {
      this.notifyListeners('remoteVideoReady', data)
    })

    this.mediaService.addEventListener('cleanedUp', (data) => {
      this.notifyListeners('mediaCleanedUp', data)
    })
  }

  // Media service public methods
  async requestMediaAccess(mediaType = 'both') {
    return await this.mediaService.requestMediaAccess(mediaType)
  }

  async initMediaSource(mediaType = 'both', conversationId) {
    return await this.mediaService.initMediaSource(mediaType, conversationId)
  }

  toggleAudioMute() {
    return this.mediaService.toggleAudioMute()
  }

  toggleVideoPause() {
    return this.mediaService.toggleVideoPause()
  }

  // Set last chat ID in localStorage
  setLastChatId(conversationId) {
    try {
      if (conversationId) {
        localStorage.setItem('candidate-lastChatId', conversationId)
      }
    } catch (error) {
      console.error('Error setting last chat ID in localStorage:', error)
    }
  }

  // Clear last chat ID from localStorage (used when chat is intentionally ended)
  clearLastChatId() {
    try {
      localStorage.removeItem('candidate-lastChatId')
    } catch (error) {
      console.error('Error clearing last chat ID from localStorage:', error)
    }
  }

  // Send data channel message to coach
  sendDataChannelMessage(messageType, _messageData) {
    try {
      if (!this.connectionState.activeCall) {
        return false
      }

      const coachEasyrtcid = this.connectionState.activeCall.coachEasyrtcid
      if (!coachEasyrtcid) {
        return false
      }

      const easyrtc = this.rtcService.getEasyRTC()
      if (!easyrtc) {
        return false
      }

      const conversationId = this.connectionState.activeCall.conversationId

      easyrtc.sendData(
        coachEasyrtcid,
        messageType,
        this.connectionState.myEasyrtcid,
        undefined,
        conversationId,
      )
      return true
    } catch (error) {
      console.error(
        'CandidateChatManager - Error sending data channel message:',
        error,
      )
      return false
    }
  }

  // Get last chat ID from localStorage
  getLastChatId() {
    try {
      return localStorage.getItem('candidate-lastChatId')
    } catch (error) {
      console.error('Error getting last chat ID from localStorage:', error)
      return null
    }
  }

  // Save chat data to localStorage (legacy method - uses new helper)
  saveChatDataToSession() {
    try {
      // Try to get conversation ID from activeCall first, then from lastChatId
      const conversationId =
        this.connectionState.activeCall?.conversationId || this.getLastChatId()

      if (conversationId) {
        this.saveChatDataToSessionStorage(conversationId, {
          selectedCoach: this.connectionState.selectedCoach,
          messages: this.connectionState.messages || [],
          chatMode: this.connectionState.chatMode,
          callType:
            this.connectionState.activeCall?.callType ||
            this.connectionState.chatMode,
          disconnectionReason: this.connectionState.disconnectionReason,
          disconnectionTimestamp: this.connectionState.disconnectionTimestamp,
          chatEndedBy: this.connectionState.chatEndedBy,
          chatWasStarted: this.connectionState.chatWasStarted,
        })
        // console.log(
        //   'Chat data saved to localStorage for conversation:',
        //   conversationId,
        //   'selectedCoach:',
        //   this.connectionState.selectedCoach,
        // )
      } else {
        console.warn('No conversation ID available for saving chat data')
      }
    } catch (error) {
      console.error('Error saving chat data to localStorage:', error)
    }
  }

  // Chat control methods
  endChat() {
    try {
      // Store values before any async operations to avoid null reference
      const conversationId = this.connectionState.activeCall?.conversationId
      const coachEasyrtcid = this.connectionState.activeCall?.coachEasyrtcid
      const wasWaitingForCoach =
        this.connectionState.chatStatus === 'waiting_for_coach'
      const wasInitiatingCall =
        this.connectionState.chatStatus === 'initiating_call'
      // When starting video/audio, chatStatus can be 'obtaining_video' (or 'obtaining_audio')
      // while media is being initialized but before the coach has accepted. In that case we
      // still want to treat endChat() as cancelling a ringing call so the coach’s ringtone
      // and pending-call UI clear just like for a pure text chat.
      const wasObtainingMedia =
        this.connectionState.chatStatus === 'obtaining_video' ||
        this.connectionState.chatStatus === 'obtaining_audio'
      const wasChatActive = this.connectionState.chatStatus === 'chat_active'
      const chatWasStarted = this.connectionState.chatWasStarted
      const isCallConnected =
        this.connectionState.activeCall?.status === 'connected'

      // If we're waiting for coach or initiating call and haven't started a chat, send call_cancelled instead of chat_ended
      // Also check chatWasStarted - if chat was started (coach accepted), we should go to chat_ended, not call_cancelled
      // Also check if the call is connected - if it's connected, the chat was started
      if (
        (wasWaitingForCoach || wasInitiatingCall || wasObtainingMedia) &&
        !wasChatActive &&
        !chatWasStarted &&
        !isCallConnected
      ) {
        const easyrtc = this.rtcService.getEasyRTC()
        if (easyrtc && coachEasyrtcid) {
          try {
            easyrtc.sendData(
              coachEasyrtcid,
              'call_cancelled',
              this.connectionState.myEasyrtcid,
              undefined,
              conversationId,
            )
          } catch (error) {
            console.error(
              'ChatManager - Error sending call_cancelled message:',
              error,
            )
          }
        }

        // Clean up the active call
        this.callService.cleanup()
        this.connectionState.activeCall = null
        this._releaseChatMedia(conversationId)

        // Set status to indicate chat was cancelled before starting
        this.connectionState.chatStatus = 'call_cancelled'
        this.connectionState.chatEndedBy = 'candidate'
        this.connectionState.chatWasStarted = false

        // Notify listeners
        this.notifyListeners('stateChange', this.connectionState)

        // Don't proceed with chat_ended flow - just return
        return
      }

      // Always log chat_ended event (candidate-specific) only if chat was actually started
      // Check chatWasStarted or isCallConnected, not just wasChatActive, since chatStatus might not be 'chat_active' yet
      if (chatWasStarted || isCallConnected) {
        // Use setTimeout to ensure this happens after any immediate navigation
        setTimeout(() => {
          this.logEvent(
            'chat_ended',
            {
              candidate: this.connectionState.username || 'unknown',
              candidate_token: this.connectionState.username || 'unknown',
            },
            conversationId, // This can be null/undefined, logEvent should handle it
          )
        }, 0)
      }

      // Send chat ended message to coach if we have an active call and chat was started
      // Check chatWasStarted or isCallConnected, not just wasChatActive, since chatStatus might not be 'chat_active' yet
      if (
        this.connectionState.activeCall &&
        (chatWasStarted || isCallConnected)
      ) {
        const easyrtc = this.rtcService.getEasyRTC()
        if (easyrtc && coachEasyrtcid) {
          try {
            easyrtc.sendData(
              coachEasyrtcid,
              'chat_ended',
              this.connectionState.myEasyrtcid,
              undefined,
              conversationId,
            )
          } catch (error) {
            console.error(
              'ChatManager - Error sending chat_ended message:',
              error,
            )
          }
        }

        // Send "left_chat" action to coach (legacy compatibility)
        // Only send if not already sent by unload handler
        if (!this._leftChatActionSent) {
          this.sendAction({
            action_type: 'left_chat',
          })
          this._leftChatActionSent = true
        }

        // Clean up the active call
        this.callService.cleanup()
        this.connectionState.activeCall = null
      }

      this._releaseChatMedia(conversationId)

      // Always save chat data and update status
      this.saveChatDataToSession()

      // Update presence to reflect chat ended status based on chat mode
      if (this.rtcService) {
        const chatMode = this.connectionState.chatMode || 'text'
        const selectedCoach = this.connectionState.selectedCoach
        const coachName =
          selectedCoach?.displayName || selectedCoach?.username || 'coach'

        if (chatMode === 'video') {
          this.rtcService.updateCandidatePresenceVideoChatEnded(
            coachName,
            conversationId,
          )
        } else if (chatMode === 'audio') {
          this.rtcService.updateCandidatePresenceAudioChatEnded(
            coachName,
            conversationId,
          )
        } else {
          this.rtcService.updateCandidatePresenceTextChatEnded(
            coachName,
            conversationId,
          )
        }
      }

      // Update connection state and notify listeners
      this.connectionState.chatStatus = 'chat_ended'
      this.connectionState.chatEndedBy = 'candidate'
      // Preserve chatWasStarted if it's already true (chat was started), otherwise use wasChatActive
      // This ensures we don't overwrite a true value with false
      this.connectionState.chatWasStarted =
        chatWasStarted === true ? true : wasChatActive

      // Clear connectionLost flag when candidate intentionally ends chat
      // This prevents the "lost connection" view from flashing
      this.connectionState.connectionLost = false
      this.connectionState.disconnectionReason = null
      this.connectionState.disconnectionTimestamp = null

      // Persist the last conversationId in memory for redirect logic,
      // while still clearing candidate-lastChatId for the next chat.
      if (conversationId) {
        this.connectionState.lastConversationId = conversationId
        this.clearLastChatId()
      }

      this.notifyListeners('stateChange', this.connectionState)

      // Leave the room so the coach candidates list does not keep showing
      // this candidate as "Chat Ended" while the survey is open.
      this.disconnect()
    } catch (error) {
      console.error('ChatManager - Error ending chat:', error)
    }
  }

  // Handle when coach ends the chat
  handleChatEndedByCoach(_data) {
    try {
      // Don't override call_missed status with chat_ended
      if (this.connectionState.chatStatus === 'call_missed') {
        return
      }

      // Store conversationId before cleaning up activeCall
      const conversationId = this.connectionState.activeCall?.conversationId

      // Ensure "coach ended the chat" appears as last message (race-safe if coach_action arrived after chat_ended)
      const messages = this.connectionState.messages || []
      const lastMessage = messages[messages.length - 1]
      const lastIsEndedChat =
        lastMessage?.type === 'coach_action' &&
        (lastMessage?.actionType === 'ended_chat' ||
          lastMessage?.actionData?.action_type === 'ended_chat')
      if (!lastIsEndedChat) {
        const coachName =
          this.connectionState.selectedCoach?.displayName ||
          this.connectionState.selectedCoach?.username ||
          'Coach'
        this.addMessage({
          id: Date.now(),
          type: 'coach_action',
          actionType: 'ended_chat',
          actionData: { action_type: 'ended_chat' },
          text: 'ended the chat',
          timestamp: new Date().toISOString(),
          sender: 'coach',
          senderName: coachName,
        })
      }

      // Save chat data to localStorage before cleaning up
      this.saveChatDataToSession()

      // Clean up the active call
      if (this.connectionState.activeCall) {
        this.callService.cleanup()
        this.connectionState.activeCall = null
      }

      this._releaseChatMedia(conversationId)

      // Update presence to reflect chat ended status based on chat mode
      if (this.rtcService) {
        const chatMode = this.connectionState.chatMode || 'text'
        const selectedCoach = this.connectionState.selectedCoach
        const coachName =
          selectedCoach?.displayName || selectedCoach?.username || 'coach'

        if (chatMode === 'video') {
          this.rtcService.updateCandidatePresenceVideoChatEnded(
            coachName,
            conversationId,
          )
        } else if (chatMode === 'audio') {
          this.rtcService.updateCandidatePresenceAudioChatEnded(
            coachName,
            conversationId,
          )
        } else {
          this.rtcService.updateCandidatePresenceTextChatEnded(
            coachName,
            conversationId,
          )
        }
      }

      // Update connection state to indicate chat ended
      this.connectionState.chatStatus = 'chat_ended'
      this.connectionState.chatEndedBy = 'coach'
      this.connectionState.chatWasStarted = true // Coach can only end if chat was started

      // Notify listeners of the state change
      this.notifyListeners('stateChange', this.connectionState)

      // Leave the room so the coach candidates list does not keep showing
      // this candidate as "Chat Ended" while the survey is open.
      this.disconnect()
    } catch (error) {
      console.error('ChatManager - Error handling chat ended by coach:', error)
    }
  }

  // Handle coach disconnection (room-based detection)
  handleCoachDisconnection(data) {
    try {
      // Try to get conversationId from activeCall first, then fallback to lastChatId
      const conversationId =
        this.connectionState.activeCall?.conversationId || this.getLastChatId()

      // Don't override call_missed status with disconnection
      if (this.connectionState.chatStatus === 'call_missed') {
        return
      }

      // Don't set connectionLost if the chat already ended intentionally
      // (candidate or coach). Prevents the "lost connection" view from flashing
      // when we disconnect so the candidate leaves the RTC room.
      if (this.connectionState.chatStatus === 'chat_ended') {
        return
      }

      // Prevent duplicate processing of disconnection using localStorage
      // Only check if we have a conversationId
      if (
        conversationId &&
        this.hasEventFired('coach_disconnected', conversationId)
      ) {
        return
      }
      if (conversationId) {
        this.markEventFired('coach_disconnected', conversationId)
      }

      const disconnectionReason = data.reason || 'coach_left_room'
      const disconnectionTimestamp = data.timestamp || new Date().toISOString()

      // Update connection state with disconnection details
      this.updateConnectionState({
        connectionLost: true,
        disconnectionReason: disconnectionReason,
        disconnectionTimestamp: disconnectionTimestamp,
        chatStatus: 'chat_ended',
      })

      // Save disconnection data to localStorage (only if we have conversationId)
      if (conversationId) {
        this.saveChatDataToSessionStorage(conversationId, {
          disconnectionReason: disconnectionReason,
          disconnectionTimestamp: disconnectionTimestamp,
          chatStatus: 'chat_ended',
        })

        // Log the disconnection event
        this.logEvent(
          'coach_disconnected',
          {
            reason: data.reason,
            coachEasyrtcid: data.coachEasyrtcid,
            timestamp: data.timestamp,
            chatMode: this.connectionState.chatMode,
          },
          conversationId,
        )

        // Build chat history on the backend
        this.buildChatHistory()
      }

      // Notify listeners
      this.notifyListeners('coachDisconnected', {
        reason: data.reason,
        timestamp: data.timestamp,
      })
    } catch (error) {
      console.error('ChatManager - Error handling coach disconnection:', error)
    }
  }

  // Handle coach peer disconnection (WebRTC peer connection lost)
  handleCoachPeerDisconnection(data) {
    try {
      // Try to get conversationId from activeCall first, then fallback to lastChatId
      const conversationId =
        this.connectionState.activeCall?.conversationId || this.getLastChatId()

      // Don't override call_missed status with disconnection
      if (this.connectionState.chatStatus === 'call_missed') {
        return
      }

      // Don't set connectionLost if the chat already ended intentionally
      // (candidate or coach). Prevents the "lost connection" view from flashing
      // when we disconnect so the candidate leaves the RTC room.
      if (this.connectionState.chatStatus === 'chat_ended') {
        return
      }

      // Prevent duplicate processing of disconnection using localStorage
      // Only check if we have a conversationId
      if (
        conversationId &&
        this.hasEventFired('coach_disconnected', conversationId)
      ) {
        return
      }
      if (conversationId) {
        this.markEventFired('coach_disconnected', conversationId)
      }

      const disconnectionReason = 'peer_connection_lost'
      const disconnectionTimestamp = data.timestamp || new Date().toISOString()

      // Update connection state with disconnection details
      this.updateConnectionState({
        connectionLost: true,
        disconnectionReason: disconnectionReason,
        disconnectionTimestamp: disconnectionTimestamp,
        chatStatus: 'chat_ended',
      })

      // Save disconnection data to localStorage (only if we have conversationId)
      if (conversationId) {
        this.saveChatDataToSessionStorage(conversationId, {
          disconnectionReason: disconnectionReason,
          disconnectionTimestamp: disconnectionTimestamp,
          chatStatus: 'chat_ended',
        })

        // Log the disconnection event
        this.logEvent(
          'coach_disconnected',
          {
            coachEasyrtcid: data.coachEasyrtcid,
            reason: data.reason,
            timestamp: data.timestamp,
            chatMode: this.connectionState.chatMode,
          },
          conversationId,
        )

        // Build chat history on the backend
        this.buildChatHistory()
      }

      // Notify listeners
      this.notifyListeners('coachPeerDisconnected', {
        reason: data.reason,
        timestamp: data.timestamp,
      })
    } catch (error) {
      console.error(
        'ChatManager - Error handling coach peer disconnection:',
        error,
      )
    }
  }

  // Enhanced connection state update method
  updateConnectionState(updates) {
    const newState = { ...this.connectionState, ...updates }

    // Add connection quality tracking
    if (updates.connectionLost) {
      newState.disconnectionReason = updates.disconnectionReason || 'unknown'
      newState.disconnectionTimestamp =
        updates.disconnectionTimestamp || new Date().toISOString()
    }

    this.connectionState = newState
    this.notifyListeners('connectionStateChanged', newState)
    this.notifyListeners('stateChange', newState)
  }

  _releaseChatMedia(conversationId = null) {
    const conversationIdToUse =
      conversationId ||
      this.connectionState.activeCall?.conversationId ||
      this.connectionState.lastConversationId

    this.rtcService.releaseLocalMedia({
      conversationId: conversationIdToUse,
      selfVideo: document.getElementById('selfVideo'),
      callerVideo: document.getElementById('callerVideo'),
    })

    this.mediaService.releaseLocalMedia([
      this.connectionState.localStream,
      this.callService.localStream,
      this.rtcService.getLocalStream(),
    ])
    this.callService.releaseLocalMedia()

    this.connectionState.localStream = null
    this.connectionState.remoteStream = null
    this.connectionState.hasLocalVideo = false
    this.connectionState.hasLocalAudio = false
    this.connectionState.hasRemoteVideo = false
    this.connectionState.hasRemoteAudio = false
  }

  pauseChat() {
    try {
      // Check current state - only pause if not already paused
      const mediaState = this.mediaService.getMediaState()

      if (mediaState.videoPaused) {
        return true
      }

      const result = this.toggleVideoPause()

      // Log candidate_action event for pause action
      if (this.connectionState.activeCall) {
        this.logEvent(
          'candidate_action',
          {
            who: 'candidate',
            easyrtcId: this.connectionState.myEasyrtcid || 'unknown',
            action: 'pause',
          },
          this.connectionState.activeCall.conversationId,
        )

        // Send "pause" action to coach (legacy compatibility)
        this.sendAction({
          action_type: 'pause',
          paused: true,
        })
      }

      return result
    } catch (error) {
      console.error('ChatManager - Error pausing chat:', error)
      return false
    }
  }

  unpauseChat() {
    try {
      // Check current state - only unpause if currently paused
      const mediaState = this.mediaService.getMediaState()

      if (!mediaState.videoPaused) {
        return true
      }

      const result = this.toggleVideoPause()

      // Log candidate_action event for unpause action
      if (this.connectionState.activeCall) {
        this.logEvent(
          'candidate_action',
          {
            who: 'candidate',
            easyrtcId: this.connectionState.myEasyrtcid || 'unknown',
            action: 'unpause',
          },
          this.connectionState.activeCall.conversationId,
        )

        // Send "pause" action to coach (legacy compatibility)
        this.sendAction({
          action_type: 'pause',
          paused: false,
        })
      }

      return result
    } catch (error) {
      console.error('ChatManager - Error unpausing chat:', error)
      return false
    }
  }

  muteChat() {
    try {
      // Check current state - only mute if not already muted
      const mediaState = this.mediaService.getMediaState()
      if (mediaState.audioMuted) {
        return true
      }

      const result = this.toggleAudioMute()

      // Log candidate_action event for mute action
      if (this.connectionState.activeCall) {
        this.logEvent(
          'candidate_action',
          {
            who: 'candidate',
            easyrtcId: this.connectionState.myEasyrtcid || 'unknown',
            action: 'mute',
          },
          this.connectionState.activeCall.conversationId,
        )

        // Send "mute" action to coach (legacy compatibility)
        this.sendAction({
          action_type: 'mute',
          paused: true,
        })
      }

      return result
    } catch (error) {
      console.error('ChatManager - Error muting chat:', error)
      return false
    }
  }

  unmuteChat() {
    try {
      // Check current state - only unmute if currently muted
      const mediaState = this.mediaService.getMediaState()
      if (!mediaState.audioMuted) {
        return true
      }

      const result = this.toggleAudioMute()

      // Log candidate_action event for unmute action
      if (this.connectionState.activeCall) {
        this.logEvent(
          'candidate_action',
          {
            who: 'candidate',
            easyrtcId: this.connectionState.myEasyrtcid || 'unknown',
            action: 'unmute',
          },
          this.connectionState.activeCall.conversationId,
        )

        // Send "mute" action to coach (legacy compatibility)
        this.sendAction({
          action_type: 'mute',
          paused: false,
        })
      }

      return result
    } catch (error) {
      console.error('ChatManager - Error unmuting chat:', error)
      return false
    }
  }

  async switchAudioDevice(deviceId) {
    return await this.mediaService.switchAudioDevice(deviceId)
  }

  async switchVideoDevice(deviceId) {
    return await this.mediaService.switchVideoDevice(deviceId)
  }

  getMediaStats() {
    return this.mediaService.getMediaStats()
  }

  getMediaState() {
    return this.mediaService.getMediaState()
  }

  getMediaDevices() {
    return this.mediaService.getMediaDevices()
  }

  // Getter for chatId (alias for conversationId for backward compatibility)
  get chatId() {
    return this.connectionState.activeCall?.conversationId
  }

  isConnected() {
    return this.connectionState.isConnected
  }

  cleanup() {
    this._releaseChatMedia()
    this.mediaService.cleanup()
  }

  // ⚠️ REMOVED: _showVideoLoadingState() - no longer needed with legacy pattern
  // ⚠️ REMOVED: _setupEasyRTCMedia() - no longer needed with legacy pattern
  // ⚠️ REMOVED: sendLocalStreamToCoach() - no longer needed with legacy pattern
  // ⚠️ REMOVED: _setupEasyRTCPeerConnectionListeners() - no longer needed with legacy pattern
  // Media initialization now happens in CallService.performCall() right before easyrtc.call()
  // This follows the same pattern as the legacy chat.js system
  // easyrtc.call() automatically handles stream exchange, no manual setup needed
}

// Singleton instance for candidate chat management
const candidateChatManager = new CandidateChatManager()
export default candidateChatManager
